import { useState, useEffect } from "react";
import { SubcategoryDetailProps } from "../constants/settingsStructure";
import { Label } from "@/modern/components/ui/label";
import { Card } from "@/modern/components/ui/card";
import { Button } from "@/modern/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/modern/components/ui/popover";
import { useSwivuBryntumContext } from "@/react/hooks/useSwivuBryntumContext";
import { ColorPicker } from "@/modern/components/color-picker";
import { TaskStylePreview } from "@/react/components/entityViews/contextMenus/task/TaskStylePreview";
import { MIXED_TASK_COLOR, MIXED_TASK_STYLE, eventStyles, STRIPED_TASK_STYLE } from "@/other/constants/TaskTypes";
import { TriggerKeyForStyleColor } from "@/other/constants/OtherKeys";
import { GRANTS } from "@/other/constants/PrivilegeTypes";
import { useSwivuStore } from "@/react/hooks/useSwivuStore";
import { KeyValueModel } from "@/bryntum-lib/models/KeyValueModel";
import { STORE_KEYS } from "@/other/constants/StoreKeys";

const allStyles = [MIXED_TASK_STYLE, ...eventStyles, STRIPED_TASK_STYLE];

const GeneralTaskSettingsDetail = ({ subcategory }: SubcategoryDetailProps) => {
  const { userPreferences, user } = useSwivuBryntumContext();
  const [taskColor, setTaskColor] = useState<string>(MIXED_TASK_COLOR);
  const [taskStyle, setTaskStyle] = useState<string>(MIXED_TASK_STYLE);
  const [stylePickerOpen, setStylePickerOpen] = useState(false);
  const [canEdit, setCanEdit] = useState(true);

  // Subscribe to board preferences changes (taskColor and taskStyle are board preferences)
  useSwivuStore<KeyValueModel>({
    storeId: STORE_KEYS.boardPreferencesStore,
    subscriberFn: ({ record }) => {
      if (record?.key === "taskColor" || record?.key === "taskStyle") {
        loadSettings();
      }
    }
  });

  useEffect(() => {
    loadSettings();
    checkPermissions();
  }, [userPreferences, user]);

  const loadSettings = () => {
    if (userPreferences) {
      const color = userPreferences.getSetting("taskColor", MIXED_TASK_COLOR, true) || MIXED_TASK_COLOR;
      const style = userPreferences.getSetting("taskStyle", MIXED_TASK_STYLE, true) || MIXED_TASK_STYLE;
      setTaskColor(color);
      setTaskStyle(style);
    }
  };

  const checkPermissions = () => {
    if (user) {
      // Check if user has grant_one permission
      const hasPermission = user.can(GRANTS.grant_one);
      setCanEdit(hasPermission);
    }
  };

  const handleTaskColorChange = (color: string | null) => {
    if (!userPreferences || !color) return;

    setTaskColor(color);
    userPreferences.updateSetting("taskColor", String(color), true);
    // @ts-expect-error - trigger is a Bryntum Store method not in type definition
    userPreferences.trigger(TriggerKeyForStyleColor, {});
  };

  const handleTaskStyleChange = (style: string) => {
    if (!userPreferences) return;

    setTaskStyle(style);
    userPreferences.updateSetting("taskStyle", String(style), true);
    // @ts-expect-error - trigger is a Bryntum Store method not in type definition
    userPreferences.trigger(TriggerKeyForStyleColor, {});
  };

  return (
    <div className="p-6">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-foreground mb-2">{subcategory.label}</h2>
        <p className="text-sm text-muted-foreground">
          {subcategory.description}
        </p>
      </div>

      <div className="space-y-5 max-w-3xl">
        {/* Task Appearance Settings Card */}
        <Card className="py-3 px-4">
          <div className="space-y-6">
            <div>
              <h3 className="text-base font-semibold text-foreground mb-1">Task Appearance</h3>
              <p className="text-xs text-muted-foreground">
                Set default color and style preferences for all tasks on the board
              </p>
            </div>

            <div className="space-y-4">
              {/* Task Color */}
              <div className="space-y-2">
                <Label htmlFor="task-color">Task Color</Label>
                <ColorPicker
                  value={taskColor}
                  onChange={handleTaskColorChange}
                  showMixedOption={true}
                  editable={false}
                  className="border-0 shadow-none"
                >
                  <Button
                    disabled={!canEdit}
                    type="button"
                    variant="outline"
                    className="w-full justify-start h-swivu-input"
                  >
                    {taskColor === MIXED_TASK_COLOR ? (
                      <>
                        <div
                          className="mr-2 h-5 w-5 rounded border border-border"
                          style={{
                            background: 'linear-gradient(to right, #EF5350, #EC407A, #AB47BC, #7E57C2, #5C6BC0, #42A5F5, #26C6DA, #26A69A, #66BB6A, #FFCA28)'
                          }}
                        />
                        <span className="capitalize">Mixed</span>
                      </>
                    ) : (
                      <>
                        <div
                          className="mr-2 h-5 w-5 rounded border"
                          style={{ backgroundColor: taskColor || "transparent" }}
                        />
                        {taskColor || "Select color"}
                      </>
                    )}
                  </Button>
                </ColorPicker>
                <p className="text-xs text-muted-foreground">
                  Select a default color for all tasks, or choose "Mixed" to allow individual task colors
                </p>
              </div>

              {/* Task Style */}
              <div className="space-y-2">
                <Label>Task Style</Label>
                <Popover open={stylePickerOpen} onOpenChange={setStylePickerOpen}>
                  <PopoverTrigger asChild>
                    <Button
                      type="button"
                      variant="outline"
                      className="w-full justify-start h-swivu-input"
                      disabled={!canEdit}
                    >
                      <i className="fa-light fa-palette mr-2" />
                      {taskStyle.charAt(0).toUpperCase() + taskStyle.slice(1)}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
                    <div className="max-h-[300px] overflow-y-auto p-2 space-y-1">
                      {allStyles.map((style) => (
                        <button
                          key={style}
                          type="button"
                          disabled={!canEdit}
                          onClick={() => {
                            handleTaskStyleChange(style);
                            setStylePickerOpen(false);
                          }}
                          className="w-full p-2 rounded hover:bg-accent transition-colors"
                        >
                          <TaskStylePreview
                            style={style}
                            isSelected={taskStyle === style}
                            color={taskColor === MIXED_TASK_COLOR ? '#2196F3' : taskColor}
                          />
                        </button>
                      ))}
                    </div>
                  </PopoverContent>
                </Popover>
                <p className="text-xs text-muted-foreground">
                  Choose how tasks are displayed in the schedule. "Mixed" allows individual task styles
                </p>
              </div>
            </div>

            {/* Permission Notice */}
            {!canEdit && (
              <div className="mt-4 p-3 bg-amber-50 dark:bg-amber-900/20 rounded-lg border border-amber-200 dark:border-amber-800">
                <p className="text-xs text-amber-900 dark:text-amber-100 leading-relaxed">
                  <i className="fa-regular fa-info-circle mr-1" />
                  <strong>Note:</strong> You don't have permission to modify these settings.
                  Contact a board administrator for assistance.
                </p>
              </div>
            )}
          </div>
        </Card>
      </div>
    </div>
  );
};

export default GeneralTaskSettingsDetail;
